/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    EXTRACT_INVENTORY_GRID
 * Date:         2025-12-11
 * Modified By:  Cursor
 *
 * Description:
 * Extracted inventory grid container from InventoryPage.tsx into reusable component
 * for use in both personal inventory and global search pages.
 *
 * Related Screens / Modules:
 * - client/src/components/inventory/InventoryGrid.tsx
 * - client/src/features/inventory/InventoryPage.tsx
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React from "react";
import { Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import "./InventoryGrid.scss";
import styles from "../../features/inventory/Inventory.module.scss";

// [2025-12-11] Grid: Reusable grid component interface (generic)
interface InventoryGridProps<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  renderItem: (item: T) => React.ReactNode;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  total?: number;
  currentPage?: number;
  totalPages?: number;
  onRetry?: () => void;
  // Optional: Display mode - 'grid' or 'list'
  displayMode?: "grid" | "list";
  // Optional: Pagination mode - when true, hides load-more button and shows only current batch
  paginationMode?: boolean;
  // Optional: Custom key extractor function (for items without _id)
  getItemKey?: (item: T, index: number) => string;
}

// [2025-12-11] Grid: Main grid component (generic)
export function InventoryGrid<T>({
  items,
  loading,
  error,
  hasMore,
  onLoadMore,
  renderItem,
  emptyMessage = "No items found",
  emptyIcon,
  total,
  currentPage,
  totalPages,
  onRetry,
  displayMode = "list",
  paginationMode = false,
  getItemKey,
}: InventoryGridProps<T>) {
  // Helper to get item key
  const getKey = (item: T, index: number): string => {
    if (getItemKey) return getItemKey(item, index);
    // Safely check for _id property
    const itemWithId = item as { _id?: string };
    return itemWithId._id || `item-${index}`;
  };
  // Error state
  if (error) {
    return (
      <Alert variant="danger" className="d-flex justify-content-between align-items-center">
        <span>{error}</span>
        {onRetry && (
          <Button variant="outline-danger" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  // Loading skeleton (initial load)
  if (loading && items.length === 0) {
    return (
      <div>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton-row border-bottom"></div>
        ))}
      </div>
    );
  }

  // Empty state (not loading, no items)
  if (!loading && items.length === 0) {
    return (
      <div className="empty-state text-center py-5">
        {emptyIcon}
        <h5 className="text-muted">{emptyMessage}</h5>
        <p className="p-small text-muted">Try adjusting your filters or search terms.</p>
      </div>
    );
  }

  // Items display
  if (displayMode === "grid") {
    return (
      <div className="inventory-grid-container">
        {/* Results summary */}
        {total !== undefined && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="p-small text-muted">
                {total} item{total !== 1 ? "s" : ""} found
              </span>
            </div>
          </div>
        )}

        {/* Grid layout */}
        <Row className="g-3">
          {items.map((item, index) => (
            <Col key={getKey(item, index)} xs={12} md={6} lg={4} xl={3}>
              {renderItem(item)}
            </Col>
          ))}
        </Row>

        {/* Loading spinner (for load more) */}
        {loading && items.length > 0 && !paginationMode && (
          <div className="loading-state">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading items...</p>
          </div>
        )}

        {/* Load more button - only show if not in pagination mode */}
        {!paginationMode && !loading && hasMore && items.length > 0 && (
          <div className="load-more-section">
            <Button variant="outline-primary" onClick={onLoadMore}>
              Load More
              {total !== undefined && items.length > 0 && (
                <span className="ms-2">({total - items.length} remaining)</span>
              )}
            </Button>
          </div>
        )}

        {/* End of results - only show if not in pagination mode */}
        {!paginationMode && !loading && !hasMore && items.length > 0 && (
          <div className="end-of-results">
            <p className="text-muted">No more items to load</p>
          </div>
        )}
      </div>
    );
  }

  // List display mode (matches current InventoryPage layout)
  return (
    <div className="inventory-grid-container">
      {/* List layout */}
      <div className={styles.scrollableList}>
        {items.map((item, index) => (
          <div key={getKey(item, index)}>{renderItem(item)}</div>
        ))}
      </div>

      {/* Loading spinner (for load more) */}
      {loading && items.length > 0 && !paginationMode && (
        <div className="loading-state text-center py-3">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading...</p>
        </div>
      )}

      {/* Load more button - only show if not in pagination mode */}
      {!paginationMode && !loading && hasMore && items.length > 0 && (
        <div className="load-more-section text-center py-3">
          <Button variant="outline-primary" onClick={onLoadMore} disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Loading...
              </>
            ) : (
              `Load More${total !== undefined && items.length > 0 ? ` (${total - items.length} remaining)` : ""}`
            )}
          </Button>
        </div>
      )}

      {/* End of results - only show if not in pagination mode */}
      {!paginationMode && !loading && !hasMore && items.length > 0 && (
        <div className="end-of-results text-center py-3">
          <p className="text-muted">No more items to load</p>
        </div>
      )}
    </div>
  );
}
